import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AssessmentType } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { initAssessmentReportViewOptions } from "@apps/reporting/components/assessment-report/helpers";
import PerformanceAssessmentChart from "@apps/reporting/components/assessment-report/performance-assessment-chart";
import PerformanceAssessmentTable from "@apps/reporting/components/assessment-report/performance-assessment-table";
import AssessmentReportViewOptionsSidebar from "@apps/reporting/components/assessment-report/report-filters/assessment-report-view-options-sidebar";
import {
  filterPerformanceReportRows,
  updateFilters,
} from "@apps/reporting/components/assessment-report/report-filters/helpers";
import { AssessmentReportFilterType } from "@apps/reporting/components/assessment-report/report-filters/types";
import useAssessmentReport from "@apps/reporting/components/assessment-report/use-assessment-report";
import CalibrationGroupsModal from "@apps/reporting/components/calibration-groups/calibration-groups-modal";
import { useNoEmployeesSelectedDialog } from "@apps/reporting/components/calibration-groups/hooks";
import useCalibrationGroupPicker, {
  ALL_CALIBRATION_GROUPS_VALUE,
  CALIBRATION_GROUP_URL_PARAM,
  USERS_NOT_IN_CALIBRATION_GROUP_VALUE,
} from "@apps/reporting/components/use-calibration-group-picker";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import useLabel from "@apps/use-label/use-label";
import { isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import CalibrationGroupList from "./calibration-group-list";

/**
 * The Calibration tab of a review. It opens on the list of groups; a click on
 * a group opens its 3×3 grid and a table with only the talent indicators and
 * the calibration status, with a picker to switch to another group.
 * The open group is in the URL, so the Back button and a shared link work.
 * The backend returns only the groups the viewer may see. "All groups" and
 * "Employees not in a group" are for admins only. An admin filters the people
 * not in a group in the Results tab's sidebar, and makes a group of them.
 */
const CalibrationTab = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const location = useLocation();
  const link = useLink();
  const report = useAssessmentReport(
    AssessmentType.Performance,
    complianceProgramId,
    { withSavedViews: false },
  );
  const { complianceProgram, viewOptions, talentIndicatorQuestions } = report;
  const {
    calibrationGroupsEnabled,
    calibrationGroups,
    isLoadingCalibrationGroups,
    calibrationGroupsError,
    calibrationGroupOptions,
    selectedCalibrationGroupId,
    urlCalibrationGroupId,
    usersNotInCalibrationGroupCount,
    calibrationGroupRows,
  } = useCalibrationGroupPicker({
    complianceProgramId,
    complianceProgram,
    // A non-admin always has one of their groups picked.
    requireGroup: !isAdmin,
    pickFromUrl: true,
  });
  // The people of the group dialog; null when it is closed.
  const [createGroupUsers, setCreateGroupUsers] = useState<BasicUser[] | null>(
    null,
  );
  const {
    openModal: openNoEmployeesSelectedDialog,
    InfoDialog: NoEmployeesSelectedDialog,
  } = useNoEmployeesSelectedDialog();

  const groupPickerOptions = useMemo(
    () =>
      isAdmin
        ? [
            { label: "All groups", value: ALL_CALIBRATION_GROUPS_VALUE },
            ...calibrationGroupOptions,
          ]
        : calibrationGroupOptions.filter(
            ({ value }) => value !== USERS_NOT_IN_CALIBRATION_GROUP_VALUE,
          ),
    [isAdmin, calibrationGroupOptions],
  );
  // A URL value the viewer cannot pick, or none, shows the list.
  const openGroupOption = groupPickerOptions.find(
    ({ value }) => value === urlCalibrationGroupId,
  );
  // The location of the list (null) or of one group, with the other params kept.
  const getGroupLocation = (value: number | null) => {
    const params = new URLSearchParams(location.search);
    if (value === null) {
      params.delete(CALIBRATION_GROUP_URL_PARAM);
    } else {
      params.set(CALIBRATION_GROUP_URL_PARAM, `${value}`);
    }
    const search = params.toString();
    return {
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    };
  };
  const getGroupUrl = (value: number | null) => {
    const { pathname, search } = getGroupLocation(value);
    return `${pathname}${search}`;
  };
  const showGroup = (value: number | null, { replace = false } = {}) => {
    // The picker replaces the URL: Back then returns to the list.
    if (replace) {
      link.replace(getGroupLocation(value));
    } else {
      // An object, not a string: after a deploy, redirect loads the new build.
      link.redirect(getGroupLocation(value));
    }
  };

  // A click on a cell of the grid filters the table to that cell. On "Not in
  // a group", an admin also has the filters of the Results tab's sidebar.
  // The filters are not saved as a view.
  const clearFilters = () => {
    const { filters } = viewOptions;
    if (
      filters.list.length === 0 &&
      filters.type === initAssessmentReportViewOptions.filters.type
    ) {
      return;
    }
    report.handleUpdateViewOptions({
      filters: initAssessmentReportViewOptions.filters,
    });
  };
  // The filters belong to the group they were set in. Clear them when the
  // open group in the URL changes, so the browser's Back and Forward buttons
  // clear them too, not only the clicks on this page.
  const clearFiltersRef = useRef(clearFilters);
  clearFiltersRef.current = clearFilters;
  const hideFiltersSidebarRef = useRef(report.handleHideViewOptionsSidebar);
  hideFiltersSidebarRef.current = report.handleHideViewOptionsSidebar;
  const previousUrlGroupIdRef = useRef(urlCalibrationGroupId);
  // The group ids when the dialog opened from "Not in a group"; see below.
  const groupIdsBeforeCreateRef = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (previousUrlGroupIdRef.current === urlCalibrationGroupId) {
      return;
    }
    previousUrlGroupIdRef.current = urlCalibrationGroupId;
    groupIdsBeforeCreateRef.current = null;
    clearFiltersRef.current();
    hideFiltersSidebarRef.current();
  }, [urlCalibrationGroupId]);
  const tableRows = useMemo(
    () =>
      filterPerformanceReportRows(calibrationGroupRows, viewOptions.filters),
    [calibrationGroupRows, viewOptions.filters],
  );

  // After the dialog saves a group of the people not in a group, show the
  // list with the new group: it shows once a new id is in the refetched
  // groups. The dialog closes before that refetch ends.
  const showGroupRef = useRef(showGroup);
  showGroupRef.current = showGroup;
  useEffect(() => {
    const groupIdsBefore = groupIdsBeforeCreateRef.current;
    if (
      !groupIdsBefore ||
      calibrationGroups.every(({ id }) => groupIdsBefore.has(id))
    ) {
      return;
    }
    groupIdsBeforeCreateRef.current = null;
    showGroupRef.current(null);
  }, [calibrationGroups]);
  const createGroupFromTableRows = () => {
    if (tableRows.length === 0) {
      openNoEmployeesSelectedDialog();
      return;
    }
    groupIdsBeforeCreateRef.current = new Set(
      calibrationGroups.map(({ id }) => id),
    );
    setCreateGroupUsers(tableRows.map((row) => row.user));
  };
  const hasSavedGroupRef = useRef(false);
  const closeCreateGroupDialog = () => {
    // On Cancel, a group that another admin makes later does not open the list.
    if (!hasSavedGroupRef.current) {
      groupIdsBeforeCreateRef.current = null;
    }
    hasSavedGroupRef.current = false;
    setCreateGroupUsers(null);
  };

  const fixedColumnIds = useMemo(
    () => [
      "name",
      "manager",
      // eslint-disable-next-line local-rules/use-label
      "team",
      ...talentIndicatorQuestions.map(({ id }) => id),
      "calibration-status",
      "calibration-group",
    ],
    [talentIndicatorQuestions],
  );

  const context = useMemo(
    () => ({
      complianceProgramId,
      complianceProgram,
      chartLabels: report.chartLabels,
      viewOptions,
      calibrationGroups: {
        data: calibrationGroups,
        showColumn: calibrationGroups.length > 0,
      },
      // The grid counts only the people of the picked group.
      userRows: calibrationGroupRows,
    }),
    [
      complianceProgramId,
      complianceProgram,
      report.chartLabels,
      viewOptions,
      calibrationGroups,
      calibrationGroupRows,
    ],
  );

  // The report query is lazy: before it starts, nothing is loading yet.
  const hasError = !!report.reportError || !!calibrationGroupsError;
  const isLoading =
    !hasError &&
    (report.isLoadingProgram ||
      !report.hasLoadedReport ||
      (isLoadingCalibrationGroups && calibrationGroups.length === 0));
  if (
    !hasError &&
    !isLoading &&
    (!complianceProgram || !report.assessmentTemplate)
  ) {
    return null;
  }
  const hasGroups = calibrationGroupsEnabled && calibrationGroups.length > 0;
  // With no groups yet, an admin still opens "Not in a group" to make one.
  const isAdminOnPeopleNotInGroup =
    isAdmin &&
    calibrationGroupsEnabled &&
    urlCalibrationGroupId === USERS_NOT_IN_CALIBRATION_GROUP_VALUE;
  // Only talent indicators that a manager answers and that the review shows.
  const hasTalentIndicatorQuestions = talentIndicatorQuestions.length > 0;
  // The sidebar's filters with a value. A filter with no item picked yet, or
  // an Enrollment Date with no date ([null, null]), filters nobody out. The
  // grid cell's filter is not in the sidebar.
  const activeFilterCount = viewOptions.filters.list.filter(
    ({ type, data }) =>
      type !== AssessmentReportFilterType.ANSWER &&
      Array.isArray(data) &&
      data.some((item) => item !== null),
  ).length;
  // Filters only on "Not in a group": nobody leaves another group from here.
  const showFilters =
    isAdmin &&
    selectedCalibrationGroupId === USERS_NOT_IN_CALIBRATION_GROUP_VALUE;

  return (
    <PerformanceAssessmentReportContext.Provider value={context}>
      <NoEmployeesSelectedDialog />
      {createGroupUsers && (
        <CalibrationGroupsModal
          complianceProgramId={complianceProgramId}
          isCreatingNew
          calibrationGroups={calibrationGroups}
          filteredUsers={createGroupUsers}
          onSave={() => {
            hasSavedGroupRef.current = true;
          }}
          onClose={closeCreateGroupDialog}
        />
      )}
      {hasError ? (
        <div className="px-8 py-16 text-center text-sm text-gray-500">
          Could not load the calibration. Reload the page to try again.
        </div>
      ) : isLoading ? (
        <div className="flex justify-center p-10">
          <Loading>Loading...</Loading>
        </div>
      ) : !hasTalentIndicatorQuestions ? (
        <div className="px-8 py-16 text-center text-sm text-gray-500">
          {`This ${label("review")} has no talent indicators for managers to rate.`}
        </div>
      ) : !hasGroups && !isAdminOnPeopleNotInGroup ? (
        <div className="flex flex-col items-center gap-4 px-8 py-16 text-center">
          <div className="text-sm text-gray-500">
            {isAdmin
              ? "No calibration groups yet"
              : `You are not in any calibration group of this ${label("review")}.`}
          </div>
          {isAdmin && calibrationGroupsEnabled && (
            <Button
              theme={buttonTheme.primary}
              text="Create group"
              onClick={() => showGroup(USERS_NOT_IN_CALIBRATION_GROUP_VALUE)}
            />
          )}
        </div>
      ) : !openGroupOption ? (
        <div className="px-8 pb-12">
          <CalibrationGroupList
            calibrationGroups={calibrationGroups}
            // Admins only: a last row for the people of the review in no group.
            peopleWithoutGroupCount={
              isAdmin ? usersNotInCalibrationGroupCount : null
            }
            getGroupUrl={getGroupUrl}
            onOpenGroup={showGroup}
            onCreateGroup={
              isAdmin
                ? () => showGroup(USERS_NOT_IN_CALIBRATION_GROUP_VALUE)
                : undefined
            }
          />
        </div>
      ) : (
        <div className="px-8 pb-12">
          <button
            type="button"
            className="mb-3 text-sm font-medium text-blue-600 hover:underline"
            onClick={() => showGroup(null)}
          >
            ← Groups
          </button>
          <div className="mb-4 flex flex-wrap gap-2">
            <ComboboxGeneric
              className="max-w-[400px]"
              prefix="Calibration Group"
              options={groupPickerOptions}
              onChangeValue={({ value }) => showGroup(value, { replace: true })}
              value={openGroupOption}
            />
            {/* More than two talent indicators: pick the grid's two axes. */}
            {talentIndicatorQuestions.length > 2 &&
              (["X", "Y"] as const).map((axis) => {
                const ownIndex =
                  viewOptions.nineBox[`selected${axis}QuestionIndex`];
                const otherIndex =
                  viewOptions.nineBox[
                    `selected${axis === "X" ? "Y" : "X"}QuestionIndex`
                  ];
                const options = talentIndicatorQuestions
                  .map(({ title }, index) => ({ label: title, value: index }))
                  .filter(({ value }) => value !== otherIndex);
                return (
                  <ComboboxGeneric
                    key={axis}
                    className="max-w-[300px]"
                    prefix={axis === "X" ? "Horizontal axis" : "Vertical axis"}
                    options={options}
                    onChangeValue={({ value }) =>
                      report.handleUpdateViewOptions({
                        nineBox: {
                          ...viewOptions.nineBox,
                          [`selected${axis}QuestionIndex`]: value,
                        },
                        // A cell filter of the old axis no longer applies.
                        filters: {
                          ...viewOptions.filters,
                          list: updateFilters(
                            viewOptions.filters.list,
                            AssessmentReportFilterType.ANSWER,
                            [],
                          ),
                        },
                      })
                    }
                    value={
                      options.find(({ value }) => value === ownIndex) ?? null
                    }
                  />
                );
              })}
          </div>
          {showFilters && calibrationGroupRows.length > 0 && (
            <>
              {report.showViewOptionsSidebar && (
                <AssessmentReportViewOptionsSidebar
                  filtersOnly
                  assessmentType={AssessmentType.Performance}
                  calibrationGroupsEnabled={calibrationGroupsEnabled}
                  isOngoingProgram={!!complianceProgram?.ongoing}
                  viewOptions={viewOptions}
                  talentIndicatorQuestions={talentIndicatorQuestions}
                  onUpdateViewOptions={report.handleUpdateViewOptions}
                  onClose={report.handleHideViewOptionsSidebar}
                  onCreateCalibrationGroup={createGroupFromTableRows}
                  // No view to save on this tab: filtersOnly hides these.
                  onResetViewToBaseline={
                    report.handleResetViewOptionsToBaseline
                  }
                  onSaveView={report.handleSaveExistingView}
                  onSaveAsNewView={report.handleSaveAsNewView}
                  onDuplicateView={report.handleDuplicateView}
                  onDeleteView={report.handleDeleteView}
                />
              )}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                {/* The Results tab's View Options icon, with a label so it is not missed. */}
                <Button
                  className="flex items-center gap-1.5"
                  onClick={report.handleShowViewOptionsSidebar}
                >
                  <TopicflowIcon type="adjustments" className="h-4 w-4" />
                  {activeFilterCount > 0
                    ? `Filters (${activeFilterCount})`
                    : "Filters"}
                </Button>
                <span className="text-sm tabular-nums text-gray-500">
                  {`${tableRows.length} of ${calibrationGroupRows.length} people`}
                </span>
                <Button
                  theme={buttonTheme.primary}
                  text={
                    tableRows.length === 0
                      ? "Create group"
                      : tableRows.length === 1
                        ? "Create group from this person"
                        : `Create group from these ${tableRows.length} people`
                  }
                  onClick={createGroupFromTableRows}
                />
              </div>
            </>
          )}
          {calibrationGroupRows.length === 0 ? (
            // The rows are the people the viewer may calibrate, never the
            // viewer: the list above counts everyone in the review.
            <div className="px-8 py-16 text-center text-sm text-gray-500">
              {selectedCalibrationGroupId !==
              USERS_NOT_IN_CALIBRATION_GROUP_VALUE
                ? "No one in this group is yours to calibrate."
                : calibrationGroups.length === 0
                  ? `No one in this ${label("review")} has a manager ${label("review")} yet.`
                  : "Everyone you can calibrate is in a calibration group."}
            </div>
          ) : showFilters && tableRows.length === 0 ? (
            // The grid is hidden and the sidebar does not list the grid
            // cell's filter: this button clears it too.
            <div className="flex flex-col items-center gap-4 px-8 py-16 text-center">
              <div className="text-sm text-gray-500">
                No one matches these filters.
              </div>
              <Button text="Clear filters" onClick={clearFilters} />
            </div>
          ) : (
            <>
              <PerformanceAssessmentChart
                questions={talentIndicatorQuestions}
                showPeople
                onChangeAnswerFilterList={(list) =>
                  report.handleUpdateViewOptions({
                    filters: {
                      ...viewOptions.filters,
                      list: updateFilters(
                        viewOptions.filters.list,
                        AssessmentReportFilterType.ANSWER,
                        list,
                      ),
                    },
                  })
                }
              />
              <PerformanceAssessmentTable
                calibrationGroupsEnabled={calibrationGroupsEnabled}
                questions={report.questions}
                filteredTableRows={tableRows}
                talentIndicatorQuestions={talentIndicatorQuestions}
                showViewOptionsSidebar={false}
                hiddenRowCount={calibrationGroupRows.length - tableRows.length}
                recentAnswerQuestionIds={report.recentAnswerQuestionIds}
                prevAnswersByAssessmentId={report.prevAnswersByAssessmentId}
                // "Clear All Filters" clears the filters: the grid cell's and,
                // on "Not in a group", the sidebar's. The grid's axes and the
                // picked group stay: they are not filters on this tab.
                onUpdateSelectedCalibrationGroup={keepPickedGroup}
                onUpdateViewOptions={report.handleUpdateViewOptions}
                onCloseViewOptionsSidebar={report.handleHideViewOptionsSidebar}
                onUpdateChartLabels={report.setChartLabels}
                onResetViewToBaseline={report.handleResetViewOptionsToBaseline}
                onResetViewToEmpty={clearFilters}
                onSaveView={report.handleSaveExistingView}
                onSaveAsNewView={report.handleSaveAsNewView}
                onDuplicateView={report.handleDuplicateView}
                onDeleteView={report.handleDeleteView}
                onCreateCalibrationGroup={createGroupFromTableRows}
                fixedColumnIds={fixedColumnIds}
                showMarkCalibratedButton
                // An approver gets no "Move to Group..." in the row menu.
                showMoveToCalibrationGroup={isAdmin}
              />
            </>
          )}
        </div>
      )}
    </PerformanceAssessmentReportContext.Provider>
  );
};

const keepPickedGroup = () => {};

export default CalibrationTab;
